import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";

import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TaskManagementLayout from "./modules/task-management/layout/TaskManagementLayout";
import DashboardPage from "./modules/task-management/pages/DashboardPage";
import TasksPage from "./modules/task-management/pages/TasksPage";
import TaskDetailsPage from "./modules/task-management/pages/TaskDetailsPage";
import { isLoggedIn } from "./config/auth";
import { scheduleTokenRefresh } from "./config/api";
import ProjectsPage from "./modules/task-management/pages/ProjectsPage";
import MomsPage from "./modules/task-management/pages/MomsPage";
import UsersPage from "./modules/task-management/pages/UsersPage";
import AuditPage from "./modules/task-management/pages/AuditPage";
import PendingValidationPage from "./modules/task-management/pages/PendingValidationPage";
import ProjectDetailsPage from "./modules/task-management/pages/ProjectDetailsPage";
import ProfilePage from "./pages/ProfilePage";
import DepartmentsPage from "./modules/task-management/pages/DepartmentsPage";
import SettingsPage from "./pages/SettingsPage";
import TimelineRequestsPage from "./modules/task-management/pages/TimelineRequestsPage";
import MyTeamPage from "./modules/task-management/pages/MyTeamPage";
import SuiteModulePage from "./pages/SuiteModulePage";
import AppSelectionPage from "./pages/AppSelectionPage";
import CrmLayout from "./modules/crm/layout/CrmLayout";
import PipelinePage from "./modules/crm/pages/PipelinePage";
import AccountsPage from "./modules/crm/pages/AccountsPage";
import ContactsPage from "./modules/crm/pages/ContactsPage";
import ActivitiesPage from "./modules/crm/pages/ActivitiesPage";
import CrmReportsPage from "./modules/crm/pages/CrmReportsPage";
import HrmsLayout from "./modules/hrms/layout/HrmsLayout";
import HrmsDashboardPage from "./modules/hrms/pages/HrmsDashboardPage";
import EmployeesPage from "./modules/hrms/pages/EmployeesPage";
import AttendancePage from "./modules/hrms/pages/AttendancePage";
import LeavesPage from "./modules/hrms/pages/LeavesPage";
import FinanceLayout from "./modules/finance/layout/FinanceLayout";
import FinanceDashboardPage from "./modules/finance/pages/FinanceDashboardPage";
import InvoicesPage from "./modules/finance/pages/InvoicesPage";
import ExpensesPage from "./modules/finance/pages/ExpensesPage";
import VendorsPage from "./modules/finance/pages/VendorsPage";
import BudgetsPage from "./modules/finance/pages/BudgetsPage";
import AdminRequestsPage from "./modules/finance/pages/AdminRequestsPage";
import ClientPortalLayout from "./modules/client-portal/layout/ClientPortalLayout";
import ClientPortalDashboardPage from "./modules/client-portal/pages/ClientPortalDashboardPage";
import ClientAccountsPage from "./modules/client-portal/pages/ClientAccountsPage";
import ClientProjectsPage from "./modules/client-portal/pages/ClientProjectsPage";
import ClientRequestsPage from "./modules/client-portal/pages/ClientRequestsPage";
import ClientApprovalsPage from "./modules/client-portal/pages/ClientApprovalsPage";
import ClientDocumentsPage from "./modules/client-portal/pages/ClientDocumentsPage";
import ClientInvoicesPage from "./modules/client-portal/pages/ClientInvoicesPage";

function App() {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());

  useEffect(() => {
    const handler = () => setLoggedIn(isLoggedIn());
    window.addEventListener("authChanged", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("authChanged", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  // Proactively refresh the JWT ~2 min before expiry so the user is never
  // logged out mid-session. Cancelled automatically on logout.
  useEffect(() => {
    if (!loggedIn) return;
    return scheduleTokenRefresh();
  }, [loggedIn]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={<LoginPage onLogin={() => setLoggedIn(true)} />}
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/apps"
          element={loggedIn ? <AppSelectionPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/crm"
          element={loggedIn ? <CrmLayout /> : <Navigate to="/login" />}
        >
          <Route index element={<Navigate to="/crm/pipeline" replace />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="activities" element={<ActivitiesPage />} />
          <Route path="reports" element={<CrmReportsPage />} />
        </Route>
        <Route
          path="/hrms"
          element={loggedIn ? <HrmsLayout /> : <Navigate to="/login" />}
        >
          <Route index element={<Navigate to="/hrms/dashboard" replace />} />
          <Route path="dashboard" element={<HrmsDashboardPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="attendance" element={<AttendancePage />} />
          <Route path="leaves" element={<LeavesPage />} />
        </Route>
        <Route
          path="/finance"
          element={loggedIn ? <FinanceLayout /> : <Navigate to="/login" />}
        >
          <Route index element={<Navigate to="/finance/dashboard" replace />} />
          <Route path="dashboard" element={<FinanceDashboardPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="budgets" element={<BudgetsPage />} />
          <Route path="approvals" element={<AdminRequestsPage />} />
        </Route>
        <Route
          path="/client-portal"
          element={loggedIn ? <ClientPortalLayout /> : <Navigate to="/login" />}
        >
          <Route index element={<Navigate to="/client-portal/dashboard" replace />} />
          <Route path="dashboard" element={<ClientPortalDashboardPage />} />
          <Route path="clients" element={<ClientAccountsPage />} />
          <Route path="projects" element={<ClientProjectsPage />} />
          <Route path="requests" element={<ClientRequestsPage />} />
          <Route path="approvals" element={<ClientApprovalsPage />} />
          <Route path="documents" element={<ClientDocumentsPage />} />
          <Route path="invoices" element={<ClientInvoicesPage />} />
        </Route>

        {/* Protected Area */}
        <Route
          path="/"
          element={loggedIn ? <TaskManagementLayout /> : <Navigate to="/login" />}
        >
          <Route index element={<Navigate to="/apps" />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Tasks */}
          <Route path="tasks" element={<TasksPage />} />
          <Route path="tasks/:taskId" element={<TaskDetailsPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailsPage />} />
          <Route path="moms" element={<MomsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="departments" element={<DepartmentsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="validations" element={<PendingValidationPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="timeline-requests" element={<TimelineRequestsPage />} />
          <Route path="team" element={<MyTeamPage />} />
          <Route path="finance-admin" element={<Navigate to="/finance" replace />} />
          <Route path="client-portal" element={<Navigate to="/client-portal" replace />} />
          <Route path="reports" element={<SuiteModulePage module="reports" />} />
          <Route path="marketplace" element={<SuiteModulePage module="marketplace" />} />
        </Route>

        {/* Fallback */}
        <Route
          path="*"
          element={<Navigate to={loggedIn ? "/apps" : "/login"} />}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
